import type { UseQueryOptions } from "@tanstack/react-query";
import { mapKeys } from "lodash";

import Sentry from "@similarweb/sentry-logger";

import type { IKeywordGroupDataForTracking } from "app/pages/keyword-analysis/KeywordsGroupsTracking";
import { KeywordsGroupsTracking } from "app/pages/keyword-analysis/KeywordsGroupsTracking";
import { DefaultFetchService } from "app/services/fetchService";
import queryClient from "single-spa/react-app/query-client";

import type {
    IUserKeywordGroup,
    IInitialKeywordGroup,
    IShareKeywordGroupPayload,
    IDuplicateKeywordGroupPayload,
} from "../keywordGroups.types";
import { KEYWORD_GROUPS_ENDPOINT, KEYWORD_GROUP_PREFIX } from "../utils/keywordGroupsTransforms";

const fetchService = DefaultFetchService.getInstance();

/**
 * @internal
 * do not use this function in the app
 */
export const keywordGroupsKeys = {
    all: ["keywordGroups"] as const,
    user: () => [...keywordGroupsKeys.all, "user"] as const,
    shared: () => [...keywordGroupsKeys.all, "shared"] as const,
    byId: (id: string) => [...keywordGroupsKeys.all, "byId", id] as const,
} as const;

const transformFirstLetter = (word: string, transformer: (char: string) => string): string => {
    return word.replace(word[0], transformer(word[0]));
};

const fromDataStoreShape = (list: any[]): IUserKeywordGroup[] => {
    return list.map((item) =>
        mapKeys(item, (val, key) => transformFirstLetter(key, (l: string) => l.toUpperCase())),
    ) as IUserKeywordGroup[];
};

const fetchUserKeywordGroups = async (): Promise<IUserKeywordGroup[]> => {
    try {
        const { OwnerGroups, SharedGroups } = await fetchService.get<{
            OwnerGroups: IUserKeywordGroup[];
            SharedGroups: IUserKeywordGroup[];
        }>(KEYWORD_GROUPS_ENDPOINT, undefined, {
            preventAutoCancellation: true,
        });
        // TODO: REMOVE THIS AFTER THE ENDPOINT IS FIXED
        // because the endpoint returns shared groups as well, we need to set the shared groups in the query client
        await queryClient.setQueryData(keywordGroupsKeys.shared(), SharedGroups.filter(Boolean));
        return OwnerGroups.filter(Boolean);
    } catch (error) {
        Sentry.error("Error fetching user keyword groups", error as Error);
        return [];
    }
};

const fetchSharedKeywordGroups = async (): Promise<IUserKeywordGroup[]> => {
    try {
        const response = await fetchService.get(`${KEYWORD_GROUPS_ENDPOINT}/shared`, undefined, {
            preventAutoCancellation: true,
        });
        return Array.isArray(response) ? fromDataStoreShape(response) : [];
    } catch (error) {
        Sentry.error("Error fetching shared keyword groups", error as Error);
        return [];
    }
};

const fetchKeywordGroupById = async (id: string): Promise<IUserKeywordGroup | null> => {
    if (!id) {
        return null;
    }

    try {
        const [userGroups, sharedGroups] = await Promise.all([
            queryClient.fetchQuery(keywordGroupsOptions.user()),
            queryClient.fetchQuery(keywordGroupsOptions.shared()),
        ]);

        const userGroup = userGroups.find((group: IUserKeywordGroup) => group.Id === id);
        if (userGroup) {
            return userGroup;
        }

        const sharedGroup = sharedGroups.find((group: IUserKeywordGroup) => group.Id === id);
        return sharedGroup || null;
    } catch (error) {
        Sentry.error("Error fetching keyword group by id", error as Error, { id });
        return null;
    }
};

/**
 * @internal
 * do not use this function in the app
 */
export const createKeywordGroup = async (group: IInitialKeywordGroup): Promise<void> => {
    const result = await fetchService.post(KEYWORD_GROUPS_ENDPOINT, group);
    const allGroups: IUserKeywordGroup[] = result as IUserKeywordGroup[];
    const added = allGroups?.find((kg) => kg.Name === group.Name);

    if (added) {
        KeywordsGroupsTracking.genericGroupTracker.onCreated(added as IKeywordGroupDataForTracking);
    }
};

/**
 * @internal
 * do not use this function in the app
 */
export const updateKeywordGroup = async (group: IUserKeywordGroup): Promise<void> => {
    await fetchService.put(KEYWORD_GROUPS_ENDPOINT, group);
};

/**
 * @internal
 * do not use this function in the app
 */
export const deleteKeywordGroup = async (
    id: string,
    name: string,
    keywords: string[],
): Promise<IUserKeywordGroup[]> => {
    const cleanId = id.startsWith(KEYWORD_GROUP_PREFIX) ? id.replace(KEYWORD_GROUP_PREFIX, "") : id;
    const result = await fetchService.delete<IUserKeywordGroup[]>(`${KEYWORD_GROUPS_ENDPOINT}?Id=${cleanId}`);
    KeywordsGroupsTracking.genericGroupTracker.onDeleted({ Id: id, Name: name, Keywords: keywords });
    return Array.isArray(result) ? result : [];
};

/**
 * @internal
 * for internal use only
 * do not use this function in the app
 */
export const shareKeywordGroup = async (
    groupId: string,
    payload: IShareKeywordGroupPayload,
): Promise<IUserKeywordGroup> => {
    const cleanPayload = Object.fromEntries(
        Object.entries(payload).filter((entry) => entry[1] !== null && entry[1] !== undefined),
    );

    return await fetchService.put(`${KEYWORD_GROUPS_ENDPOINT}/share/${groupId}`, cleanPayload);
};

/**
 * @internal
 * do not use this function in the app
 */
export const removeKeywordGroupSharing = async (groupId: string): Promise<any> => {
    return await fetchService.delete(`${KEYWORD_GROUPS_ENDPOINT}/share/${groupId}`);
};

/**
 * @internal
 * do not use this function in the app
 */
export const duplicateKeywordGroup = async (payload: IDuplicateKeywordGroupPayload): Promise<IUserKeywordGroup> => {
    return await fetchService.put<IUserKeywordGroup>(`${KEYWORD_GROUPS_ENDPOINT}/duplicate`, {
        keywordGroupId: payload.KeywordGroupId,
        keywordGroupNewName: payload.KeywordGroupNewName,
    });
};

const THIRTY_MINUTES = 30 * 60 * 1000;

/**
 * @internal
 * do not use this function in the app
 */
export const keywordGroupsOptions: Record<string, (...args: any[]) => UseQueryOptions<any>> = {
    user: () => ({
        queryKey: keywordGroupsKeys.user(),
        queryFn: fetchUserKeywordGroups,
        staleTime: THIRTY_MINUTES,
        gcTime: THIRTY_MINUTES,
        retry: 1,
    }),

    shared: () => ({
        queryKey: keywordGroupsKeys.shared(),
        queryFn: fetchSharedKeywordGroups,
        staleTime: THIRTY_MINUTES,
        gcTime: THIRTY_MINUTES,
        retry: 1,
    }),

    byId: (id: string) => ({
        queryKey: keywordGroupsKeys.byId(id),
        queryFn: () => fetchKeywordGroupById(id),
        staleTime: THIRTY_MINUTES,
        gcTime: THIRTY_MINUTES,
        retry: 1,
        enabled: !!id,
    }),
} as const;
